/**
 * /fair-launch/market/:chainId/:tokenAddress — Market page.
 *
 * Shows the bonding curve state for a single fair-launch token:
 *  - Token identity and metadata
 *  - Bonding curve stats (price, reserves, graduation progress)
 *  - Trade history
 *  - Buy/sell panel — live quotes via quoteBuy/quoteSell, tx via buy/sell
 *
 * When no data is found via the API, renders a "not indexed" placeholder.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { decodeEventLog } from "viem";
import { useQuoteRefresh } from "../hooks/useQuoteRefresh";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Button, Card, Pill } from "../components/ui";
import { WalletButton } from "../components/WalletButton";
import { formatEth } from "../lib/format";
import { ACTIVE_CHAIN } from "../config/chains";
import { useWallet } from "../wallet/WalletContext";
import { robinhoodTestnet } from "../wallet/chain";
import { bondingCurveMarketAbi, erc20ApproveAbi } from "../config/bondingCurveMarketAbi";
import { PriceChart } from "../components/PriceChart";
import { friendlyError } from "../lib/tradeErrors";
import { ShareButton } from "../components/ShareButton";
import { MarketComments } from "../components/MarketComments";
import { AddEthPanel } from "../components/AddEthPanel";
import { useTradeRecord, persistTradeStatus } from "../hooks/useTradeRecord";

// ── Types ──────────────────────────────────────────────────────────────────────

interface FairLaunchMarket {
  id: string;
  chainId: number;
  tokenAddress: string;
  marketAddress: string;
  creatorAddress: string;
  name: string;
  symbol: string;
  realEthReserve: string;
  tokenReserve: string;
  graduated: boolean;
  paused: boolean;
  graduatedAt?: string | null;
  poolAddress?: string | null;
  lpTokenAddress?: string | null;
  isSimulation?: boolean | null;
  txHash: string;
  blockNumber: string;
  createdAt: string;
  updatedAt: string;
  // Oracle attestation fields — present when indexer returns them
  virtualEthReserve?: string | null;
  graduationThreshold?: string | null;
  graduationValuationUsd?: string | null;
  virtualReserveUsd?: string | null;
  oraclePrice?: string | null;
  oracleDecimals?: number | null;
  oracleUpdatedAt?: string | null;
  // oracleRoundId == "0" means market was created via emergency fallback oracle
  oracleRoundId?: string | null;
  isTestnetScaled?: boolean | null;
}

interface Trade {
  id: string;
  traderAddress: string;
  direction: "buy" | "sell";
  ethAmount: string;
  tokenAmount: string;
  feeAmount: string;
  blockTimestamp: string;
  txHash: string;
}

// Fallback constants for when the API hasn't yet returned oracle-derived fields.
// These are not used for on-chain logic — the market stores the real values.
const FALLBACK_VIRTUAL_ETH = 1_500_000_000_000_000_000n; // 1.5 ETH @ $2k/ETH

/** Minimum ETH for a buy (mirrors BondingCurveMarket.MIN_BUY_ETH). */
const MIN_BUY_ETH_WEI = 100_000_000_000_000n; // 0.0001 ETH

/** Default slippage tolerance (1% = 100 bps). */
const SLIPPAGE_BPS = 100n;

/** localStorage key used to persist the trader's slippage preference. */
export const LS_SLIPPAGE_KEY = "synapz:slippage_bps";

/**
 * Read the trader's previously saved slippage from localStorage.
 * Falls back to SLIPPAGE_BPS (100 bps) on any parse/range error so the
 * feature degrades gracefully in environments where localStorage is blocked.
 * Valid range: 1–5000 bps (0.01%–50%).
 */
export function readStoredSlippage(): bigint {
  try {
    const stored = localStorage.getItem(LS_SLIPPAGE_KEY);
    if (!stored) return SLIPPAGE_BPS;
    const n = parseInt(stored, 10);
    if (!isFinite(n) || n < 1 || n > 5000) return SLIPPAGE_BPS;
    return BigInt(n);
  } catch {
    return SLIPPAGE_BPS;
  }
}

/** Format a basis-point value as a percentage string, e.g. 100n → "1.0%". */
export function formatSlippage(bps: bigint): string {
  return `${(Number(bps) / 100).toFixed(1)}%`;
}

/**
 * Threshold (in basis points) above which a fresh submit-time quote is
 * considered a meaningful price move that warrants showing the trader a notice.
 * 50 bps = 0.5 %.
 */
const PRICE_MOVED_THRESHOLD_BPS = 50n;

/**
 * Threshold (in basis points) above which a high-slippage warning banner is
 * shown in the quote panel. 500 bps = 5 %. Values above this are almost always
 * a fat-finger mistake and expose the trader to MEV sandwich attacks.
 */
const HIGH_SLIPPAGE_WARNING_BPS = 500n;

/** Deadline offset from now in seconds. */
const DEADLINE_OFFSET_S = 300;

function shortAddr(addr: string) { return `${addr.slice(0, 6)}…${addr.slice(-4)}`; }
function explorerTx(tx: string) {
  return `${ACTIVE_CHAIN.explorerUrl}/tx/${tx}`;
}
function explorerAddr(addr: string) {
  return `${ACTIVE_CHAIN.explorerUrl}/address/${addr}`;
}

// ── Input parsing helpers ──────────────────────────────────────────────────────

/** Parse a decimal ETH string to wei, or null if invalid. */
function parseEthInput(value: string): bigint | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  if (!isFinite(n) || n <= 0) return null;
  try {
    // Convert to wei with 18 decimal precision
    const parts = trimmed.split(".");
    const whole = parts[0] ?? "0";
    const fracPadded = (parts[1] ?? "").slice(0, 18).padEnd(18, "0");
    return BigInt(whole) * 10n ** 18n + BigInt(fracPadded);
  } catch {
    return null;
  }
}

/** Parse a whole-token string to smallest units (×1e18), or null if invalid. */
function parseTokenInput(value: string): bigint | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  if (!isFinite(n) || n <= 0) return null;
  try {
    const parts = trimmed.split(".");
    const whole = parts[0] ?? "0";
    const fracPadded = (parts[1] ?? "").slice(0, 18).padEnd(18, "0");
    return BigInt(whole) * 10n ** 18n + BigInt(fracPadded);
  } catch {
    return null;
  }
}

/** Format a bigint token amount (18 decimals) to a human-readable string. */
function formatTokens(wei: bigint): string {
  const whole = wei / 10n ** 18n;
  return whole.toLocaleString();
}

/** Convert a bigint wei amount to a decimal ETH string suitable for the input field. */
function weiToEthInputString(wei: bigint): string {
  if (wei <= 0n) return "0";
  const whole = wei / 10n ** 18n;
  const frac  = wei % 10n ** 18n;
  const fracStr = frac.toString().padStart(18, "0").replace(/0+$/, "");
  return fracStr ? `${whole}.${fracStr}` : `${whole}`;
}

/** Convert a bigint token amount (18 decimals) to a decimal string for the input field. */
function weiToTokenInputString(wei: bigint): string {
  if (wei <= 0n) return "0";
  const whole = wei / 10n ** 18n;
  const frac  = wei % 10n ** 18n;
  const fracStr = frac.toString().padStart(18, "0").replace(/0+$/, "");
  return fracStr ? `${whole}.${fracStr}` : `${whole}`;
}

/** Gas reserve subtracted from ETH balance when the trader clicks Max on the buy tab. */
const GAS_RESERVE_WEI = 1_000_000_000_000_000n; // 0.001 ETH

// friendlyError is imported from lib/tradeErrors.ts

/**
 * Attempts to decode the revert reason for a reverted transaction by replaying
 * it at the block it was mined in via eth_call. Returns the decoded reason
 * string when available, or null when no reason can be extracted.
 *
 * This is a best-effort enrichment — all failures are silently swallowed so
 * the caller always falls back to a generic message.
 */
async function decodeRevertReason(
  publicClient: ReturnType<typeof useWallet>["publicClient"],
  hash: `0x${string}`,
  blockNumber: bigint,
): Promise<string | null> {
  let tx: Awaited<ReturnType<typeof publicClient.getTransaction>>;
  try {
    tx = await publicClient.getTransaction({ hash });
  } catch {
    // Network or RPC error fetching the transaction — nothing to decode.
    return null;
  }
  try {
    await publicClient.call({
      to: tx.to ?? undefined,
      data: tx.input,
      value: tx.value,
      account: tx.from,
      blockNumber,
    });
    // eth_call did not revert — no extra reason available.
    return null;
  } catch (callErr: unknown) {
    const msg = callErr instanceof Error ? callErr.message : String(callErr);
    // Ignore the bare "execution reverted" string — it carries no extra detail.
    if (!msg || msg.trim() === "execution reverted") return null;
    return msg;
  }
}

// ── Receipt log parsing ───────────────────────────────────────────────────────

/**
 * Attempts to decode the exact received amount from a transaction receipt's
 * event logs.
 *
 * - Buy: scans for the `Buy` event emitted by `marketAddr` where
 *   `buyer == traderAddr` and returns `tokensOut`.
 * - Sell: scans for the `Sell` event emitted by `marketAddr` where
 *   `seller == traderAddr` and returns `netEth`.
 *
 * Returns `null` when no matching log is found or decoding fails (network
 * error, unexpected log format, etc.) — the caller should fall back to the
 * pre-trade estimate in that case.
 *
 * Exported for unit testing.
 */
export function parseReceiptAmount(
  logs: readonly { address: `0x${string}`; topics: readonly `0x${string}`[]; data: `0x${string}` }[],
  marketAddr: `0x${string}`,
  tab: "buy" | "sell",
  traderAddr: `0x${string}`,
): bigint | null {
  try {
    const eventName = tab === "buy" ? "Buy" : "Sell";
    const lowerMarket = marketAddr.toLowerCase();
    const lowerTrader = traderAddr.toLowerCase();
    for (const log of logs) {
      if (log.address.toLowerCase() !== lowerMarket) continue;
      try {
        const decoded = decodeEventLog({
          abi: bondingCurveMarketAbi,
          eventName,
          topics: log.topics as [`0x${string}`, ...`0x${string}`[]],
          data: log.data,
        });
        if (tab === "buy") {
          const { buyer, tokensOut } = decoded.args as unknown as {
            buyer: `0x${string}`;
            tokensOut: bigint;
          };
          if (buyer.toLowerCase() === lowerTrader) return tokensOut;
        } else {
          const { seller, netEth } = decoded.args as unknown as {
            seller: `0x${string}`;
            netEth: bigint;
          };
          if (seller.toLowerCase() === lowerTrader) return netEth;
        }
      } catch {
        // Wrong event selector or malformed log — try the next one.
        continue;
      }
    }
    return null;
  } catch {
    return null;
  }
}

// ── Graduation progress bar ───────────────────────────────────────────────────

function GradProgress({ realEth, graduationThreshold, graduationValuationUsd }: {
  realEth: bigint;
  graduationThreshold: bigint | null;
  graduationValuationUsd: string | null | undefined;
}) {
  if (graduationThreshold === null) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-muted">
          <span>Graduation progress</span>
          <span className="font-mono text-muted/40">threshold loading…</span>
        </div>
        <div className="h-2 bg-line/30 rounded-full" />
      </div>
    );
  }

  const pct = realEth >= graduationThreshold ? 100
    : Number((realEth * 10000n) / graduationThreshold) / 100;

  const usdLabel = graduationValuationUsd
    ? `${parseInt(graduationValuationUsd).toLocaleString()} USD target`
    : "";

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted">
        <span>Graduation progress {usdLabel && <span className="text-plasma/70 ml-1">({usdLabel})</span>}</span>
        <span className="font-mono">
          {formatEth(realEth)} / {formatEth(graduationThreshold)}
        </span>
      </div>
      <div className="h-2 bg-line/30 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-plasma/60 to-plasma rounded-full transition-all duration-500"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <p className="text-xs text-right text-muted/60">{pct.toFixed(2)}%</p>
    </div>
  );
}

// ── Spot price calculation (from reserves) ────────────────────────────────────

function calcSpotPrice(realEth: bigint, tokenReserve: bigint, virtualEth: bigint): string {
  if (tokenReserve === 0n) return "—";
  // price = (V + R) / T in ETH per whole token (T is in 1e18 units)
  const ethTotalWei   = virtualEth + realEth;        // wei
  const pricePerUnit  = (ethTotalWei * (10n ** 18n)) / tokenReserve; // wei per unit
  const nanoEth = Number(pricePerUnit) / 1e9; // nanoETH per token
  return `${nanoEth.toExponential(3)} nETH/token`;
}

/** Returns true when the market was created via the emergency fallback oracle (roundId == 0). */
function isFallbackOracle(market: FairLaunchMarket): boolean {
  // oracleRoundId is null when not yet backfilled from the contract.
  // Only treat it as fallback when it has been read and is definitively "0".
  return market.oracleRoundId === "0";
}

/**
 * Warning badge displayed on markets whose graduation threshold was computed
 * from the emergency fallback oracle price rather than a live Chainlink round.
 */
function FallbackOracleBadge({ inline = false }: { inline?: boolean }) {
  const label = "⚠ Emergency price used at launch";
  if (inline) {
    return (
      <span
        className="inline-block rounded-full border border-warn/50 bg-warn/10 text-warn px-2.5 py-0.5 text-xs font-medium"
        title="This market's graduation threshold was set using an emergency fallback price — the live Chainlink oracle was unavailable at creation time. The threshold is locked immutably in the contract and will not change."
      >
        {label}
      </span>
    );
  }
  return (
    <div
      role="alert"
      className="rounded-lg border border-warn/40 bg-warn/8 p-4 space-y-2"
      style={{ background: "rgba(251,191,36,0.06)" }}
    >
      <div className="flex items-start gap-2">
        <span className="text-warn text-base leading-none mt-0.5" aria-hidden="true">⚠</span>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-warn">Emergency price used at launch</p>
          <p className="text-xs text-warn/80 leading-relaxed">
            The Chainlink ETH/USD oracle was unavailable when this market was created.
            The graduation threshold was computed from a fallback price instead of a
            live Chainlink round. This threshold is stored immutably in the contract
            and <strong className="text-warn/90">will not change</strong> — it may differ
            from what an oracle-backed market would have produced. Trade with this in mind.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Format oracle price to USD string given raw answer + decimals. */
function formatOraclePrice(price: string | null | undefined, decimals: number | null | undefined): string {
  if (!price || decimals == null) return "—";
  try {
    const usd = Number(BigInt(price)) / Math.pow(10, decimals);
    return `${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch { return "—"; }
}

// ── Trade row ─────────────────────────────────────────────────────────────────

function TradeRow({ t }: { t: Trade }) {
  const isBuy   = t.direction === "buy";
  const ethAmt  = BigInt(t.ethAmount || "0");
  const tokenAmt = BigInt(t.tokenAmount || "0");
  const ts      = new Date(t.blockTimestamp).toLocaleTimeString();

  return (
    <tr className="border-b border-line/20 last:border-0 text-xs">
      <td className={`px-3 py-2 font-mono font-semibold ${isBuy ? "text-plasma" : "text-danger/80"}`}>
        {isBuy ? "BUY" : "SELL"}
      </td>
      <td className="px-3 py-2 font-mono text-muted">{formatEth(ethAmt)}</td>
      <td className="px-3 py-2 font-mono text-muted text-right">
        {(Number(tokenAmt) / 1e18).toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </td>
      <td className="px-3 py-2 font-mono text-muted/60">
        <a href={explorerTx(t.txHash)} target="_blank" rel="noreferrer"
          className="hover:text-plasma transition-colors">
          {shortAddr(t.txHash)}↗
        </a>
      </td>
      <td className="px-3 py-2 text-muted/50">{ts}</td>
    </tr>
  );
}

// ── Trading panel ─────────────────────────────────────────────────────────────

type TradeStatus =
  | { status: "idle" }
  | { status: "approving" }
  | { status: "signing" }
  | { status: "confirming"; hash: `0x${string}` }
  | { status: "success"; hash: `0x${string}` }
  | { status: "error"; message: string };

interface TradeQuote {
  tokensOut?: bigint; // for buy
  netEth?: bigint;    // for sell
  fee: bigint;
}

// Exported so tab-visibility tests can mount the hook-owning component directly.
export function TradingPanel({ market }: { market: FairLaunchMarket }) {
  const [tab, setTab] = useState<"buy" | "sell">("buy");
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState<TradeQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [tradeStatus, setTradeStatus] = useState<TradeStatus>({ status: "idle" });
  const [ethBalance, setEthBalance] = useState<bigint | null>(null);
  const [tokenBalance, setTokenBalance] = useState<bigint | null>(null);
  // True while the post-trade balance refresh is in-flight; Max is hidden during this window.
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  // Add-ETH bridge panel
  const [showAddEth, setShowAddEth] = useState(false);
  const [addEthShortfall, setAddEthShortfall] = useState<bigint | null>(null);
  // Monotonic counter — prevents a stale RPC response from overwriting a newer one.
  const refreshSeqRef = useRef(0);

  const { address, isConnected, chainId, publicClient, walletClient } = useWallet();
  const onCorrectChain = chainId === ACTIVE_CHAIN.chainId;
  const canTrade = isConnected && onCorrectChain;

  const tokenAddr = market.tokenAddress as `0x${string}`;

  // ── Balance refresh ──────────────────────────────────────────────────────────

  const refreshBalances = useCallback(async () => {
    if (!address || !onCorrectChain) {
      setEthBalance(null);
      setTokenBalance(null);
      return;
    }
    // Skip while the tab is hidden — the visibility-change effect will catch up.
    if (typeof document !== "undefined" && document.hidden) return;
    // Monotonic request ID: discard this result if a newer call has already started.
    const seq = ++refreshSeqRef.current;
    const acct = address as `0x${string}`;
    const [eth, tok] = await Promise.allSettled([
      publicClient.getBalance({ address: acct }),
      publicClient.readContract({
        address: tokenAddr,
        abi: erc20ApproveAbi,
        functionName: "balanceOf",
        args: [acct],
      }),
    ]);
    if (seq !== refreshSeqRef.current) return; // stale — a newer request won
    setEthBalance(eth.status === "fulfilled" ? (eth.value as bigint) : null);
    setTokenBalance(tok.status === "fulfilled" ? (tok.value as bigint) : null);
  }, [address, onCorrectChain, publicClient, tokenAddr]);

  // Clear balances immediately when the wallet address changes so a previous
  // account's balance is never shown under the new account — even briefly
  // while the re-fetch is still in-flight.
  useEffect(() => {
    setEthBalance(null);
    setTokenBalance(null);
  }, [address]);

  // Fetch on connect / chain change.
  useEffect(() => { void refreshBalances(); }, [refreshBalances]);

  // Re-fetch after a successful trade and gate Max until the refresh resolves.
  useEffect(() => {
    if (tradeStatus.status !== "success") return;
    let cancelled = false;
    setBalanceRefreshing(true);
    void refreshBalances().finally(() => {
      if (!cancelled) setBalanceRefreshing(false);
    });
    return () => { cancelled = true; };
  }, [tradeStatus.status, refreshBalances]);

  // Catch-up fetch when the tab becomes visible again after being hidden.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const handleVisibilityChange = () => {
      if (!document.hidden) void refreshBalances();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshBalances]);

  // Block-triggered balance refresh — keeps ETH and token balances live while
  // the trading panel is visible, so the Max button always reflects the wallet's
  // actual balance (e.g. after an external transfer from another dApp).
  // Mirrors the block-watcher pattern in useQuoteRefresh: skips when the tab is
  // hidden, resumes on the next block after the tab becomes visible again.
  useEffect(() => {
    if (!canTrade) return;
    const unwatch = publicClient.watchBlockNumber({
      poll: true,
      pollingInterval: 4_000,
      onBlockNumber: () => {
        if (typeof document !== "undefined" && document.hidden) return;
        void refreshBalances();
      },
    });
    return () => unwatch();
  }, [canTrade, publicClient, refreshBalances]);

  // ── Max mode ─────────────────────────────────────────────────────────────────
  // Tracks whether the current amount was populated by clicking Max. After a
  // confirmed trade refreshes the balance, max mode automatically recalculates
  // the input from the new spendable balance. Manual edits exit max mode so
  // the user's entered value is never silently overwritten.

  const [isMaxMode, setIsMaxMode] = useState(false);

  // ── Slippage tolerance ───────────────────────────────────────────────────────
  // Stored in basis points; the user can change it via the settings row in the
  // quote panel.  Defaults to SLIPPAGE_BPS (1 %).  Validated to [1, 5000] bps
  // (0.01 %–50 %) when setting from the custom input.
  // Lazy initialiser reads the saved value from localStorage so the preference
  // survives page refreshes without an extra render cycle.
  const [slippageBps, setSlippageBps] = useState<bigint>(readStoredSlippage);

  // Persist slippage preference to localStorage whenever the trader changes it.
  useEffect(() => {
    try {
      localStorage.setItem(LS_SLIPPAGE_KEY, String(slippageBps));
    } catch { /* localStorage may be blocked in some environments */ }
  }, [slippageBps]);

  useEffect(() => {
    if (!isMaxMode || balanceRefreshing) return;
    if (tab === "buy" && ethBalance !== null) {
      const maxWei = ethBalance > GAS_RESERVE_WEI ? ethBalance - GAS_RESERVE_WEI : 0n;
      setAmount(maxWei > 0n ? weiToEthInputString(maxWei) : "0");
    } else if (tab === "sell" && tokenBalance !== null) {
      setAmount(tokenBalance > 0n ? weiToTokenInputString(tokenBalance) : "0");
    }
  }, [isMaxMode, ethBalance, tokenBalance, balanceRefreshing, tab]);

  const marketAddr = market.marketAddress as `0x${string}`;

  // ── Paused guard ──────────────────────────────────────────────────────────

  if (market.paused) {
    return (
      <Card>
        <Pill tone="warn">Market paused</Pill>
        <p className="text-sm text-muted mt-2">Trading is temporarily suspended by the protocol.</p>
      </Card>
    );
  }

  // Inline subcomponent avoids re-renders of the hook-owning component, but we
  // keep the hooks at the top of TradingPanel (rules of hooks).
  return (
    <TradingPanelInner
      market={market}
      tab={tab} setTab={setTab}
      amount={amount} setAmount={setAmount}
      quote={quote} setQuote={setQuote}
      quoting={quoting} setQuoting={setQuoting}
      tradeStatus={tradeStatus} setTradeStatus={setTradeStatus}
      address={address}
      isConnected={isConnected}
      publicClient={publicClient}
      walletClient={walletClient}
      onCorrectChain={onCorrectChain}
      canTrade={canTrade}
      marketAddr={marketAddr}
      tokenAddr={tokenAddr}
      ethBalance={ethBalance}
      tokenBalance={tokenBalance}
      balanceRefreshing={balanceRefreshing}
      isMaxMode={isMaxMode}
      setIsMaxMode={setIsMaxMode}
      slippageBps={slippageBps}
      setSlippageBps={setSlippageBps}
      showAddEth={showAddEth}
      setShowAddEth={setShowAddEth}
      addEthShortfall={addEthShortfall}
      setAddEthShortfall={setAddEthShortfall}
      setEthBalance={setEthBalance}
    />
  );
}

// Inner component receives already-resolved props — no hooks here, so the
// early-return guards above never violate rules of hooks.
// Exported for unit/integration testing of the runTrade callback.
export function TradingPanelInner({
  market,
  tab, setTab,
  amount, setAmount,
  quote, setQuote,
  quoting, setQuoting,
  tradeStatus, setTradeStatus,
  address,
  isConnected,
  publicClient,
  walletClient,
  onCorrectChain,
  canTrade,
  marketAddr,
  tokenAddr,
  ethBalance,
  tokenBalance,
  balanceRefreshing,
  isMaxMode: _isMaxMode,
  setIsMaxMode,
  slippageBps = SLIPPAGE_BPS,
  setSlippageBps = () => {},
  showAddEth,
  setShowAddEth,
  addEthShortfall,
  setAddEthShortfall,
  setEthBalance,
}: {
  market: FairLaunchMarket;
  tab: "buy" | "sell"; setTab: (t: "buy" | "sell") => void;
  amount: string; setAmount: (v: string) => void;
  quote: TradeQuote | null; setQuote: (q: TradeQuote | null) => void;
  quoting: boolean; setQuoting: (b: boolean) => void;
  tradeStatus: TradeStatus; setTradeStatus: (s: TradeStatus) => void;
  address: string | null;
  isConnected: boolean;
  publicClient: ReturnType<typeof useWallet>["publicClient"];
  walletClient: ReturnType<typeof useWallet>["walletClient"];
  onCorrectChain: boolean;
  canTrade: boolean;
  marketAddr: `0x${string}`;
  tokenAddr: `0x${string}`;
  ethBalance: bigint | null;
  tokenBalance: bigint | null;
  balanceRefreshing: boolean;
  isMaxMode: boolean;
  setIsMaxMode: (v: boolean) => void;
  /** Slippage tolerance in basis points (e.g. 100n = 1 %).
   *  Optional — defaults to SLIPPAGE_BPS (1 %) so existing tests that
   *  don't pass this prop continue to work without modification. */
  slippageBps?: bigint;
  setSlippageBps?: (v: bigint) => void;
  showAddEth: boolean;
  setShowAddEth: (v: boolean) => void;
  addEthShortfall: bigint | null;
  setAddEthShortfall: (v: bigint | null) => void;
  setEthBalance: (v: bigint | null) => void;
}) {

  // ── In-flight guard (ref-based) ─────────────────────────────────────────────
  // This ref is set to true synchronously at the start of runTrade and cleared
  // in the finally block. It prevents a second simultaneous on-chain call even
  // during the async gap between entering runTrade and the first
  // setTradeStatus({ status: "signing" }) call — a window where the React
  // state-based guard cannot yet block a second click.
  const tradeInFlightRef = useRef(false);

  // ── Quote auto-refresh (block-triggered) ────────────────────────────────────
  // Delegates both the debounced user-edit path and the block-triggered refresh
  // to useQuoteRefresh. Returns lastQuotedAt/secAgo for the "Refreshed X s ago"
  // indicator in the quote box.

  const { lastQuotedAt, secAgo } = useQuoteRefresh({
    amount,
    tab,
    canTrade,
    publicClient,
    marketAddr,
    setQuote,
    setQuoting,
  });

  // ── Price-updated notice ─────────────────────────────────────────────────────
  // Set when the fresh submit-time quote differs from the displayed quote by
  // more than PRICE_MOVED_THRESHOLD_BPS. Informational only — does not block.

  const [priceUpdatedNotice, setPriceUpdatedNotice] = useState<{
    freshValue: bigint;
    direction: "buy" | "sell";
    diffBps: bigint;
    increased: boolean;
  } | null>(null);

  // ── Confirmed receipt amount ──────────────────────────────────────────────────
  // Set to the exact on-chain amount decoded from the transaction receipt after a
  // successful trade. Null means the receipt logs could not be parsed — the
  // success panel falls back to the pre-trade fresh-quote estimate in that case.

  const [confirmedReceipt, setConfirmedReceipt] = useState<bigint | null>(null);

  // Reset on tab change.
  const handleTabChange = useCallback((t: "buy" | "sell") => {
    setTab(t);
    setAmount("");
    setQuote(null);
    setTradeStatus({ status: "idle" });
    setIsMaxMode(false);
    setPriceUpdatedNotice(null);
    setConfirmedReceipt(null);
  }, [setTab, setAmount, setQuote, setTradeStatus, setIsMaxMode]);

  // ── Transaction persistence (SYN-WALLET-1) ─────────────────────────────────
  // Persists every trade lifecycle step to the server for Transaction Centre and
  // restore-on-refresh. Persistence is fire-and-forget: failures never block trades.
  const { startTrade, pendingTx, clearPending } = useTradeRecord({
    walletAddress: address,
    chainId: market.chainId,
    action: tab === "buy" ? "buy" : "sell",
    contractAddress: marketAddr,
  });

  // ── Transaction execution ────────────────────────────────────────────────────

  const runTrade = useCallback(async () => {
    if (!walletClient || !address || !quote) return;
    // Ref-based guard (synchronous): blocks concurrent calls before any await fires.
    // This catches the window between clicking the button and the first
    // setTradeStatus({ status: "signing" }) call, where the state-based guard below
    // cannot yet see that a call is already in flight.
    if (tradeInFlightRef.current) return;
    // State-based guard: never fire a new on-chain call while one is in flight or after success.
    // Success requires an explicit "New trade" reset before another trade can be submitted.
    if (tradeStatus.status === "signing" || tradeStatus.status === "confirming" ||
        tradeStatus.status === "approving" || tradeStatus.status === "success") return;

    // Persistence: generate a fresh txId for this trade attempt and persist DRAFT.
    // startTrade is fire-and-forget — API failure never blocks the trade.
    const txId = startTrade({ walletAddress: address!, chainId: market.chainId });

    tradeInFlightRef.current = true;
    const deadline = BigInt(Math.floor(Date.now() / 1000) + DEADLINE_OFFSET_S);
    const acct = address as `0x${string}`;

    try {
      if (tab === "buy") {
        const ethWei = parseEthInput(amount);
        if (!ethWei || !quote.tokensOut) return;

        // Fetch a fresh on-chain quote immediately before building the tx so
        // minTokensOut is derived from the current price, not the potentially-
        // stale display quote (which may be up to one block old).
        const freshBuyResult = await publicClient.readContract({
          address: marketAddr,
          abi: bondingCurveMarketAbi,
          functionName: "quoteBuy",
          args: [ethWei],
        });
        const [freshTokensOut] = freshBuyResult as [bigint, bigint];

        // Guard: a zero tokensOut from quoteBuy means the contract would give
        // the buyer nothing — this can happen if the curve is at max capacity,
        // the buy amount is below the contract's MIN_BUY_ETH, or the market is
        // in a state where buy() would revert.  Abort here instead of letting
        // the simulate call surface an opaque revert reason.
        if (!freshTokensOut) {
          setTradeStatus({
            status: "error",
            message: "Quote returned zero tokens — the amount may be too small or the market at capacity. Try a larger amount.",
          });
          return;
        }

        // Notify the trader if the fresh quote differs from the displayed quote
        // by more than PRICE_MOVED_THRESHOLD_BPS (0.5%).
        if (quote.tokensOut !== undefined && quote.tokensOut > 0n) {
          const increased = freshTokensOut >= quote.tokensOut;
          const diff = increased
            ? freshTokensOut - quote.tokensOut
            : quote.tokensOut - freshTokensOut;
          const diffBps = (diff * 10000n) / quote.tokensOut;
          if (diffBps >= PRICE_MOVED_THRESHOLD_BPS) {
            setPriceUpdatedNotice({ freshValue: freshTokensOut, direction: "buy", diffBps, increased });
          }
        }

        // Slippage guard: minTokensOut = freshTokensOut × (10000 − slippageBps) / 10000
        const minTokensOut = (freshTokensOut * (10000n - slippageBps)) / 10000n;

        // Simulate first — surfaces reverts (slippage, graduated, paused) before gas is spent.
        setTradeStatus({ status: "signing" });
        void persistTradeStatus(txId, "AWAITING_SIGNATURE", {
          action: "buy", walletAddress: address!, chainId: market.chainId,
          contractAddress: marketAddr, amountWei: String(ethWei),
        });
        const { request: buyRequest } = await publicClient.simulateContract({
          address: marketAddr,
          abi: bondingCurveMarketAbi,
          functionName: "buy",
          args: [minTokensOut, deadline],
          value: ethWei,
          account: acct,
        });
        const hash = await walletClient.writeContract(buyRequest);
        void persistTradeStatus(txId, "SUBMITTED", { txHash: hash, contractAddress: marketAddr });

        setTradeStatus({ status: "confirming", hash });
        const receipt = await publicClient.waitForTransactionReceipt({ hash });
        if (receipt.status !== "success") {
          const reason = await decodeRevertReason(publicClient, hash, receipt.blockNumber);
          throw new Error(reason ?? "Transaction reverted on-chain.");
        }
        // Parse the exact token amount from the Buy event in the receipt.
        // Falls back to null (estimate) if the log cannot be decoded.
        setConfirmedReceipt(parseReceiptAmount(receipt.logs ?? [], marketAddr, "buy", acct));
        setTradeStatus({ status: "success", hash });
        void persistTradeStatus(txId, "CONFIRMED", { txHash: hash, contractAddress: marketAddr });

      } else {
        // Sell: approve market to spend tokens, then sell.
        const tokensWei = parseTokenInput(amount);
        if (!tokensWei || !quote.netEth) return;

        // Defence-in-depth: re-read the live token balance from the chain
        // before touching any approval or sell path.  The submit-button gate
        // (canSubmit = false when exceedsBalance) is the primary line of
        // defence, but programmatic handler invocations, keyboard events on
        // a non-standard form, account switches, or another transaction
        // consuming the balance between render and click can all leave the
        // rendered UI state stale.  This guard fires regardless of button state.
        const liveTokenBalance = await publicClient.readContract({
          address: tokenAddr,
          abi: erc20ApproveAbi,
          functionName: "balanceOf",
          args: [acct],
        }) as bigint;
        if (liveTokenBalance === 0n) {
          setTradeStatus({ status: "error", message: `You have no ${market.symbol} tokens to sell.` });
          return;
        }
        if (tokensWei > liveTokenBalance) {
          setTradeStatus({ status: "error", message: `Insufficient ${market.symbol} balance: wallet holds ${formatTokens(liveTokenBalance)}, sell requires ${formatTokens(tokensWei)}.` });
          return;
        }

        // Check existing allowance.
        const allowance = await publicClient.readContract({
          address: tokenAddr,
          abi: erc20ApproveAbi,
          functionName: "allowance",
          args: [acct, marketAddr],
        }) as bigint;

        if (allowance < tokensWei) {
          setTradeStatus({ status: "approving" });
          const approveTx = await walletClient.writeContract({
            address: tokenAddr,
            abi: erc20ApproveAbi,
            functionName: "approve",
            args: [marketAddr, tokensWei],
            account: acct,
            chain: robinhoodTestnet,
          });
          const approveReceipt = await publicClient.waitForTransactionReceipt({ hash: approveTx });
          if (approveReceipt.status !== "success") throw new Error("Token approval reverted.");
        }

        // Fetch a fresh on-chain quote immediately before building the sell tx —
        // after any approval delay — so minEthOut is derived from the current
        // price, not a quote that may be several blocks old.
        const freshSellResult = await publicClient.readContract({
          address: marketAddr,
          abi: bondingCurveMarketAbi,
          functionName: "quoteSell",
          args: [tokensWei],
        });
        const [freshNetEth] = freshSellResult as [bigint, bigint];

        // Guard: a zero netEth from quoteSell means the contract would pay the
        // seller nothing — this can happen when the curve is fully drained,
        // the market has just graduated, or the RPC returns a malformed
        // response.  Abort here instead of proceeding to simulateContract with
        // a zero minEthOut floor, which the contract could silently accept.
        if (!freshNetEth) {
          setTradeStatus({
            status: "error",
            message: "Quote returned zero ETH — please retry.",
          });
          return;
        }

        // Notify the trader if the fresh sell quote differs from the displayed
        // quote by more than PRICE_MOVED_THRESHOLD_BPS (0.5%).
        if (quote.netEth !== undefined && quote.netEth > 0n) {
          const increased = freshNetEth >= quote.netEth;
          const diff = increased
            ? freshNetEth - quote.netEth
            : quote.netEth - freshNetEth;
          const diffBps = (diff * 10000n) / quote.netEth;
          if (diffBps >= PRICE_MOVED_THRESHOLD_BPS) {
            setPriceUpdatedNotice({ freshValue: freshNetEth, direction: "sell", diffBps, increased });
          }
        }

        const minEthOut = (freshNetEth * (10000n - slippageBps)) / 10000n;

        // Simulate first — surfaces reverts (slippage, graduated, paused) before gas is spent.
        setTradeStatus({ status: "signing" });
        void persistTradeStatus(txId, "AWAITING_SIGNATURE", {
          action: "sell", walletAddress: address!, chainId: market.chainId,
          contractAddress: marketAddr,
        });
        const { request: sellRequest } = await publicClient.simulateContract({
          address: marketAddr,
          abi: bondingCurveMarketAbi,
          functionName: "sell",
          args: [tokensWei, minEthOut, deadline],
          account: acct,
        });
        const hash = await walletClient.writeContract(sellRequest);
        void persistTradeStatus(txId, "SUBMITTED", { txHash: hash, contractAddress: marketAddr });

        setTradeStatus({ status: "confirming", hash });
        const receipt = await publicClient.waitForTransactionReceipt({ hash });
        if (receipt.status !== "success") {
          const reason = await decodeRevertReason(publicClient, hash, receipt.blockNumber);
          throw new Error(reason ?? "Transaction reverted on-chain.");
        }
        // Parse the exact ETH amount from the Sell event in the receipt.
        // Falls back to null (estimate) if the log cannot be decoded.
        setConfirmedReceipt(parseReceiptAmount(receipt.logs ?? [], marketAddr, "sell", acct));
        setTradeStatus({ status: "success", hash });
        void persistTradeStatus(txId, "CONFIRMED", { txHash: hash, contractAddress: marketAddr });
      }
    } catch (e: unknown) {
      const errMsg = friendlyError(e);
      const isRejection = /reject|cancel|denied|refuse/i.test(errMsg);
      void persistTradeStatus(txId, isRejection ? "CANCELLED" : "FAILED", { errorMessage: errMsg });
      setTradeStatus({ status: "error", message: errMsg });
    } finally {
      // Always release the in-flight lock so a future runTrade call can proceed.
      tradeInFlightRef.current = false;
    }
  }, [walletClient, address, quote, tradeStatus.status, tab, amount,
      marketAddr, tokenAddr, publicClient, setTradeStatus, market, slippageBps]);

  // ── Derived state ─────────────────────────────────────────────────────────────

  const ethWei   = tab === "buy" ? parseEthInput(amount) : null;
  const tokensWei = tab === "sell" ? parseTokenInput(amount) : null;

  const buyTooSmall = tab === "buy" && ethWei !== null && ethWei < MIN_BUY_ETH_WEI;
  const inputValid  = tab === "buy"
    ? ethWei !== null && ethWei >= MIN_BUY_ETH_WEI
    : tokensWei !== null && tokensWei > 0n;

  // Balance-exceeded guards — purely client-side, no extra RPC calls needed.
  const exceedsEthBalance   = tab === "buy"  && ethBalance !== null   && ethWei !== null   && ethWei   > ethBalance;
  const exceedsTokenBalance = tab === "sell" && tokenBalance !== null && tokensWei !== null && tokensWei > tokenBalance;
  const exceedsBalance      = exceedsEthBalance || exceedsTokenBalance;

  // Zero-balance sell guard — shown before the generic "insufficient balance" message.
  const zeroTokenBalanceSell = tab === "sell" && tokenBalance === 0n && amount.trim() !== "";

  // Zero-capacity buy guard — when the live display quote returns 0 tokensOut the
  // market is at capacity (all curve supply sold) or has just graduated.  Disable
  // the Buy button immediately so the trader isn't surprised by a post-click error.
  const zeroTokensOutBuy = tab === "buy" && quote !== null && quote.tokensOut === 0n;

  const busy = tradeStatus.status === "approving" ||
               tradeStatus.status === "signing"   ||
               tradeStatus.status === "confirming";

  // Never allow a sell submission on a graduated market — the curve is closed.
  const graduatedSell = tab === "sell" && market.graduated;

  const canSubmit = canTrade && inputValid && !quoting && !!quote && !busy &&
    tradeStatus.status !== "success" && !exceedsBalance && !zeroTokensOutBuy && !graduatedSell;

  // ── Button label ──────────────────────────────────────────────────────────────

  function btnLabel(): string {
    if (!isConnected) return "Connect wallet to trade";
    if (!onCorrectChain) return `Switch to ${ACTIVE_CHAIN.label}`;
    if (graduatedSell) return "Sells closed — use the DEX";
    if (tradeStatus.status === "approving") return "Approving tokens…";
    if (tradeStatus.status === "signing")   return "Sign in wallet…";
    if (tradeStatus.status === "confirming") return "Confirming…";
    if (zeroTokensOutBuy) return "Market at capacity";
    if (quoting) return "Getting quote…";
    return tab === "buy" ? "Buy tokens" : "Sell tokens";
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Card className="space-y-4">
      {/* Unaudited beta notice — always visible in the trading panel */}
      <div className="rounded border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger/80">
        ⚠ <strong>Unaudited beta</strong> — smart contracts have not received an independent external audit. You may lose all funds. Transactions are irreversible.
      </div>

      {/* Pending tx restore banner — shown when a non-terminal tx exists for this market */}
      {pendingTx && (
        <div className="rounded border border-amber-500/40 bg-amber-500/8 px-3 py-2 text-xs flex items-center justify-between gap-2" role="alert">
          <span className="text-amber-700 dark:text-amber-300">
            ⏳ A previous <strong>{pendingTx.action}</strong> is still pending.{" "}
            <a href="/wallet/transactions" className="underline hover:text-plasma transition-colors">
              View in Transaction Centre
            </a>
          </span>
          <button
            onClick={clearPending}
            className="shrink-0 text-muted hover:text-ink transition-colors text-xs leading-none"
            aria-label="Dismiss pending transaction notice"
          >✕</button>
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex rounded-lg border border-line overflow-hidden">
        {(["buy", "sell"] as const).map(t => (
          <button key={t} onClick={() => handleTabChange(t)}
            className={`flex-1 py-2 text-sm font-semibold transition-colors capitalize ${
              tab === t ? "bg-plasma/15 text-plasma border-plasma/40" : "text-muted hover:text-ink"
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* Graduated market — sell is closed on the bonding curve */}
      {tab === "sell" && market.graduated && (
        <div
          role="alert"
          data-testid="graduated-sell-banner"
          className="rounded-lg border border-warn/50 bg-warn/8 p-3 text-xs space-y-1"
          style={{ background: "rgba(251,191,36,0.08)" }}
        >
          <p className="font-semibold text-warn flex items-center gap-1.5">
            <span aria-hidden="true">🎓</span> This market has graduated
          </p>
          <p className="text-warn/80 leading-relaxed">
            The bonding curve is closed — tokens can no longer be sold back through this panel.
            {market.poolAddress ? (
              <>
                {" "}Trade on the DEX pool:{" "}
                <a
                  href={explorerAddr(market.poolAddress)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono underline hover:text-warn transition-colors"
                  data-testid="dex-pool-link"
                >
                  {shortAddr(market.poolAddress)}↗
                </a>
              </>
            ) : (
              " Check the DEX for this token's pool."
            )}
          </p>
        </div>
      )}

      {/* Wallet / chain gate */}
      {!isConnected ? (
        <div className="space-y-3">
          <p className="text-sm text-muted text-center">Connect your wallet to trade</p>
          <WalletButton />
        </div>
      ) : !onCorrectChain ? (
        <div className="rounded-lg border border-warn/30 bg-warn/5 p-3 text-xs text-warn/90">
          Wrong network. Please switch to <strong>{ACTIVE_CHAIN.label}</strong>.
        </div>
      ) : null}

      {/* Amount input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-ink">
          {tab === "buy" ? "ETH to spend" : `${market.symbol} tokens to sell`}
        </label>
        <input
          className={`w-full rounded-lg border bg-panel2 px-3 py-2 text-sm text-ink font-mono outline-none transition-colors ${
            canTrade ? "border-line focus:border-plasma/60" : "border-line opacity-50 cursor-not-allowed"
          }`}
          value={amount}
          onChange={e => {
            setIsMaxMode(false);
            setAmount(e.target.value);
            setQuote(null);
            setPriceUpdatedNotice(null);
            if (tradeStatus.status === "error" || tradeStatus.status === "success") {
              setTradeStatus({ status: "idle" });
            }
          }}
          placeholder={tab === "buy" ? "0.01" : "100000"}
          disabled={!canTrade || busy}
          aria-label={tab === "buy" ? "ETH amount" : "Token amount"}
        />
        {buyTooSmall && (
          <p className="text-xs text-warn/80">Minimum buy is 0.0001 ETH.</p>
        )}
        {/* Balance hint + Max button */}
        {canTrade && tab === "buy" && ethBalance !== null && (
          <div className="flex items-center justify-between text-xs text-muted/60">
            <span>Your balance</span>
            <div className="flex items-center gap-2">
              <span className="font-mono">{formatEth(ethBalance)} ETH</span>
              {!busy && !balanceRefreshing && (
                <button
                  type="button"
                  onClick={() => {
                    const maxWei = ethBalance > GAS_RESERVE_WEI ? ethBalance - GAS_RESERVE_WEI : 0n;
                    if (maxWei > 0n) {
                      setIsMaxMode(true);
                      setAmount(weiToEthInputString(maxWei));
                      setQuote(null);
                      if (tradeStatus.status === "error" || tradeStatus.status === "success") {
                        setTradeStatus({ status: "idle" });
                      }
                    }
                  }}
                  className="rounded px-1.5 py-0.5 text-xs font-semibold text-plasma border border-plasma/40 hover:bg-plasma/10 transition-colors leading-none"
                  aria-label="Fill with max ETH balance"
                >
                  Max
                </button>
              )}
            </div>
          </div>
        )}
        {canTrade && tab === "sell" && tokenBalance !== null && (
          <div className="flex items-center justify-between text-xs text-muted/60">
            <span>Your balance</span>
            <div className="flex items-center gap-2">
              <span className="font-mono">{formatTokens(tokenBalance)} {market.symbol}</span>
              {!busy && !balanceRefreshing && (
                <button
                  type="button"
                  onClick={() => {
                    if (tokenBalance > 0n) {
                      setIsMaxMode(true);
                      setAmount(weiToTokenInputString(tokenBalance));
                      setQuote(null);
                      if (tradeStatus.status === "error" || tradeStatus.status === "success") {
                        setTradeStatus({ status: "idle" });
                      }
                    }
                  }}
                  className="rounded px-1.5 py-0.5 text-xs font-semibold text-plasma border border-plasma/40 hover:bg-plasma/10 transition-colors leading-none"
                  aria-label="Fill with max token balance"
                >
                  Max
                </button>
              )}
            </div>
          </div>
        )}
        {/* Insufficient balance warnings */}
        {exceedsEthBalance && !showAddEth && (
          <div className="space-y-2">
            <p className="text-xs text-danger/90 font-medium" role="alert">
              Insufficient ETH balance: you hold {formatEth(ethBalance!)}
            </p>
            <Button
              variant="ghost"
              className="w-full text-xs"
              onClick={() => {
                setAddEthShortfall(
                  ethWei !== null && ethBalance !== null && ethWei > ethBalance
                    ? ethWei - ethBalance
                    : null,
                );
                setShowAddEth(true);
              }}
              data-testid="open-add-eth"
            >
              Add ETH to Robinhood Chain →
            </Button>
          </div>
        )}
        {showAddEth && (
          <AddEthPanel
            wallet={address}
            currentL2Balance={ethBalance}
            shortfallWei={addEthShortfall}
            returnContext="trade"
            onClose={() => setShowAddEth(false)}
            onFundsArrived={(newBalance) => {
              setShowAddEth(false);
              setEthBalance(newBalance);
            }}
          />
        )}
        {zeroTokenBalanceSell ? (
          <p className="text-xs text-danger/90 font-medium" role="alert">
            You have no {market.symbol} tokens to sell.
          </p>
        ) : exceedsTokenBalance && (
          <p className="text-xs text-danger/90 font-medium" role="alert">
            Insufficient {market.symbol} balance: you hold {formatTokens(tokenBalance!)} {market.symbol}
          </p>
        )}
      </div>

      {/* Quote display */}
      {canTrade && (
        <div className={`rounded-lg border p-3 text-xs space-y-1 transition-opacity ${
          quote ? "border-plasma/20 bg-plasma/5" : "border-line/20 bg-panel2/30"
        } ${quoting ? "opacity-60" : ""}`}>
          {quoting && (
            <p className="text-muted/60 text-center">Calculating…</p>
          )}
          {!quoting && quote && tab === "buy" && quote.tokensOut !== undefined && quote.tokensOut > 0n && (
            <>
              <div className="flex justify-between">
                <span className="text-muted">You receive ≈</span>
                <span className="font-mono text-plasma">{formatTokens(quote.tokensOut)} {market.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted/70">Min received</span>
                <span className="font-mono text-muted/70" data-testid="min-received">
                  {formatTokens((quote.tokensOut * (10000n - slippageBps)) / 10000n)} {market.symbol}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Protocol fee</span>
                <span className="font-mono text-muted/70">{formatEth(quote.fee)}</span>
              </div>
              <div className="flex justify-between items-center pt-0.5">
                <p className="text-muted/50">{formatSlippage(slippageBps)} slippage applied.</p>
                {lastQuotedAt !== null && (
                  <p className="text-muted/40 tabular-nums">
                    Refreshed {secAgo === 0 ? "just now" : `${secAgo}s ago`}
                  </p>
                )}
              </div>
            </>
          )}
          {!quoting && zeroTokensOutBuy && (
            <p className="text-center text-warn/90 text-xs font-medium" role="alert" data-testid="capacity-notice">
              Market at capacity — no tokens available to buy.
            </p>
          )}
          {!quoting && quote && tab === "sell" && quote.netEth !== undefined && (
            <>
              <div className="flex justify-between">
                <span className="text-muted">You receive ≈</span>
                <span className="font-mono text-plasma">{formatEth(quote.netEth)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted/70">Min received</span>
                <span className="font-mono text-muted/70" data-testid="min-received">
                  {formatEth((quote.netEth * (10000n - slippageBps)) / 10000n)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Protocol fee</span>
                <span className="font-mono text-muted/70">{formatEth(quote.fee)}</span>
              </div>
              <div className="flex justify-between items-center pt-0.5">
                <p className="text-muted/50">{formatSlippage(slippageBps)} slippage applied.</p>
                {lastQuotedAt !== null && (
                  <p className="text-muted/40 tabular-nums">
                    Refreshed {secAgo === 0 ? "just now" : `${secAgo}s ago`}
                  </p>
                )}
              </div>
            </>
          )}
          {!quoting && !quote && amount.trim() && inputValid && (
            <p className="text-muted/60 text-center">No quote available.</p>
          )}
          {!quoting && !quote && !amount.trim() && (
            <p className="text-muted/40 text-center">Enter an amount to see a quote.</p>
          )}
        </div>
      )}

      {/* Slippage tolerance setting — shown whenever the quote panel is visible */}
      {canTrade && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted/60 font-medium">Slippage</span>
          <div className="flex items-center gap-1">
            {([50n, 100n, 200n] as const).map((bps) => (
              <button
                key={bps}
                type="button"
                onClick={() => setSlippageBps(bps)}
                aria-label={`Set slippage to ${Number(bps) / 100}%`}
                aria-pressed={slippageBps === bps}
                className={`rounded px-1.5 py-0.5 text-xs font-semibold transition-colors leading-none border ${
                  slippageBps === bps
                    ? "bg-plasma text-ink border-plasma"
                    : "text-muted/70 border-line/40 hover:border-plasma/40 hover:text-plasma"
                }`}
              >
                {Number(bps) / 100}%
              </button>
            ))}
            {/* Custom slippage input — only visible when a non-preset value is selected */}
            <input
              type="number"
              min="0.01"
              max="50"
              step="0.1"
              inputMode="decimal"
              placeholder="Custom"
              aria-label="Custom slippage percentage"
              className="w-16 rounded border border-line/40 bg-panel/30 px-1.5 py-0.5 text-xs font-mono text-right focus:border-plasma/60 focus:outline-none"
              value={
                [50n, 100n, 200n].includes(slippageBps)
                  ? ""
                  : (Number(slippageBps) / 100).toFixed(2)
              }
              onChange={(e) => {
                const pct = parseFloat(e.target.value);
                if (isFinite(pct) && pct >= 0.01 && pct <= 50) {
                  setSlippageBps(BigInt(Math.round(pct * 100)));
                }
              }}
            />
          </div>
        </div>
      )}

      {/* High-slippage warning — shown when custom slippage exceeds 5% (500 bps).
          Preset buttons are 0.5/1/2 % and never reach this threshold. */}
      {canTrade && slippageBps > HIGH_SLIPPAGE_WARNING_BPS && (
        <div
          role="alert"
          data-testid="high-slippage-warning"
          className="rounded border border-warn/50 bg-warn/8 px-3 py-2 text-xs text-warn/90 flex items-start gap-2"
          style={{ background: "rgba(251,191,36,0.08)" }}
        >
          <span aria-hidden="true" className="mt-0.5 shrink-0">⚠</span>
          <span>
            <strong>High slippage</strong> — your trade may be front-run.
          </span>
        </div>
      )}

      {/* Sell: allowance note */}
      {canTrade && tab === "sell" && (
        <p className="text-xs text-muted/60">
          Selling requires two wallet signatures: one to approve the market contract,
          one to execute the sale.
        </p>
      )}

      {/* Price-updated notice — shown when the fresh submit-time quote differs
          from the displayed quote by more than 0.5%. Stays visible through
          approving / signing / confirming so the trader always sees the updated
          price while the transaction is in-flight. Hidden in "success" state
          because the confirmed-trade panel below renders the actual output
          amount instead. Informational only — does not block the trade. */}
      {priceUpdatedNotice && tradeStatus.status !== "success" && (
        <div
          role="status"
          className="rounded border border-warn/40 bg-warn/8 px-3 py-2 text-xs text-warn/90 flex items-start gap-2"
          style={{ background: "rgba(251,191,36,0.06)" }}
        >
          <span aria-hidden="true" className="mt-0.5 shrink-0">⚡</span>
          <span>
            <strong>Price updated</strong> — at submit time you&apos;ll receive ≈{" "}
            {priceUpdatedNotice.direction === "buy"
              ? `${formatTokens(priceUpdatedNotice.freshValue)} ${market.symbol}`
              : formatEth(priceUpdatedNotice.freshValue)
            }{" "}
            ({priceUpdatedNotice.increased ? "+" : "−"}{(Number(priceUpdatedNotice.diffBps) / 100).toFixed(1)}% from quote,{" "}
            {formatSlippage(slippageBps)} slippage still applied).
          </span>
        </div>
      )}

      {/* Tx status */}
      {tradeStatus.status === "error" && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-xs text-danger">
          {tradeStatus.message}
          <button className="ml-2 underline" onClick={() => setTradeStatus({ status: "idle" })}>
            Try again
          </button>
        </div>
      )}

      {tradeStatus.status === "confirming" && (
        <div className="text-xs text-muted">
          Confirming…{" "}
          <a
            href={`${ACTIVE_CHAIN.explorerUrl}/tx/${tradeStatus.hash}`}
            className="text-plasma hover:underline font-mono"
            target="_blank"
            rel="noreferrer"
          >
            {tradeStatus.hash.slice(0, 18)}…
          </a>
        </div>
      )}

      {tradeStatus.status === "success" && (
        <div className="rounded border border-plasma/30 bg-plasma/10 p-3 text-xs text-plasma space-y-1">
          <p className="font-semibold">
            {tab === "buy" ? "Purchase confirmed!" : "Sale confirmed!"}
          </p>
          {/* Actual output amount — prefers the exact on-chain amount decoded from
              the receipt logs (confirmedReceipt). Falls back to the fresh
              submit-time quote (priceUpdatedNotice.freshValue) or the display
              quote when the log cannot be parsed. Shows "You received" (no ≈)
              for the exact value and "You received ≈" for estimates. */}
          {tab === "buy" && (() => {
            const amt = confirmedReceipt ?? priceUpdatedNotice?.freshValue ?? quote?.tokensOut;
            if (amt == null) return null;
            return (
              <p data-testid="confirmed-output">
                {confirmedReceipt !== null ? "You received " : "You received ≈ "}
                <span className="font-mono font-semibold">
                  {formatTokens(amt)} {market.symbol}
                </span>
              </p>
            );
          })()}
          {tab === "sell" && (() => {
            const amt = confirmedReceipt ?? priceUpdatedNotice?.freshValue ?? quote?.netEth;
            if (amt == null) return null;
            return (
              <p data-testid="confirmed-output">
                {confirmedReceipt !== null ? "You received " : "You received ≈ "}
                <span className="font-mono font-semibold">
                  {formatEth(amt)}
                </span>
              </p>
            );
          })()}
          <a
            href={`${ACTIVE_CHAIN.explorerUrl}/tx/${tradeStatus.hash}`}
            className="hover:underline font-mono"
            target="_blank"
            rel="noreferrer"
          >
            View transaction ↗
          </a>
          <p className="text-muted/70 pt-1">
            The trade history below will update once the indexer picks up the new block.
          </p>
        </div>
      )}

      {/* Submit button — in success state this resets the form for a new trade
          rather than calling runTrade, preventing accidental duplicate submissions. */}
      {tradeStatus.status === "success" ? (
        <Button
          className="w-full"
          onClick={() => {
            setAmount("");
            setQuote(null);
            setTradeStatus({ status: "idle" });
            setPriceUpdatedNotice(null);
            setConfirmedReceipt(null);
            // Clear max mode so the panel starts completely fresh. Without this,
            // a max-mode flag from the previous trade would silently re-populate
            // the input from the refreshed balance on the next render cycle,
            // which could surprise traders who just want to type a new amount.
            setIsMaxMode(false);
          }}
        >
          New trade
        </Button>
      ) : (
        <Button
          className="w-full"
          disabled={!canSubmit}
          onClick={runTrade}
        >
          {btnLabel()}
        </Button>
      )}
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FairLaunchMarket() {
  const { chainId: chainIdParam, tokenAddress } = useParams<{
    chainId: string;
    tokenAddress: string;
  }>();

  const [market, setMarket]       = useState<FairLaunchMarket | null>(null);
  const [trades, setTrades]       = useState<Trade[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [tradesError, setTradesError] = useState(false);
  // tradesLoading tracks in-progress trades-only retries so the button
  // can show "Retrying…" without flashing the page-level skeleton.
  const [tradesLoading, setTradesLoading] = useState(false);
  // Incrementing retryCount re-triggers the full fetch (market + trades).
  // Incrementing tradesRetryCount re-fetches only trades — market state is
  // left untouched so the page never flickers back to the loading skeleton.
  const [retryCount, setRetryCount] = useState(0);
  const [tradesRetryCount, setTradesRetryCount] = useState(0);
  // Counts how many trader-initiated retries have also failed, so the error
  // notice can escalate the message after two or more consecutive failures.
  const [tradesFailedRetries, setTradesFailedRetries] = useState(0);

  const chainId = parseInt(chainIdParam ?? String(ACTIVE_CHAIN.chainId), 10);

  // ── Gate 8: referral detection ──────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const { address: connectedWallet } = useWallet();

  // Record referral attribution when ?ref=<address> is present.
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (!ref || !tokenAddress || !/^0x[0-9a-fA-F]{40}$/.test(ref)) return;
    // Fire-and-forget — attribution failure must never block the market page.
    void fetch("/api/fair-launch/referrals", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        chainId,
        tokenAddress: tokenAddress.toLowerCase(),
        referrerAddress: ref.toLowerCase(),
        ...(connectedWallet ? { visitorAddress: connectedWallet.toLowerCase() } : {}),
      }),
    }).catch(() => { /* attribution failures are silent */ });
  }, [searchParams, tokenAddress, chainId, connectedWallet]);

  useEffect(() => {
    if (!tokenAddress) return;
    // cancelled flag prevents stale async completions from updating state.
    // Required for React 18 StrictMode which double-invokes effects: the first
    // invocation's cleanup sets cancelled=true before its async chain resolves,
    // so only the second (canonical) invocation's state changes take effect.
    let cancelled = false;
    // Holds the pending auto-retry timer so it can be cleared if the component
    // unmounts or the effect re-runs (e.g. the user navigates to another market)
    // before the 5-second window elapses.
    let autoRetryTimer: ReturnType<typeof setTimeout> | null = null;

    setLoading(true);
    setError(null);
    setTradesError(false);
    // Reset the failed-retry counter whenever a fresh market load cycle starts
    // so a counter accumulated on a previous market (or previous load) never
    // causes the escalated message to appear before two retries have failed in
    // the current context.
    setTradesFailedRetries(0);

    const base = `/api/fair-launch`;
    const tradesUrl = `${base}/markets/${tokenAddress}/trades?chainId=${chainId}&limit=50`;
    Promise.all([
      fetch(`${base}/markets/${tokenAddress}?chainId=${chainId}`),
      fetch(tradesUrl),
    ])
      .then(async ([mRes, tRes]) => {
        if (cancelled) return;
        if (mRes.status === 404) {
          setMarket(null);
          setError("not_found");
          return;
        }
        if (!mRes.ok) throw new Error("API error");
        const m: FairLaunchMarket = await mRes.json();
        if (cancelled) return;
        setMarket(m);
        if (tRes.ok) {
          const td = await tRes.json() as { trades: Trade[] };
          if (!cancelled) {
            setTrades(td.trades);
            setTradesFailedRetries(0);
          }
        } else {
          // Silently schedule one automatic retry after 5 s so transient
          // server errors recover without the trader noticing. Only show
          // the error notice (and manual Retry button) if the auto-retry
          // also fails.
          if (!cancelled) {
            autoRetryTimer = setTimeout(() => {
              if (cancelled) return;
              fetch(tradesUrl)
                .then(async (r) => {
                  if (cancelled) return;
                  if (r.ok) {
                    const td = await r.json() as { trades: Trade[] };
                    if (!cancelled) setTrades(td.trades);
                  } else {
                    if (!cancelled) setTradesError(true);
                  }
                })
                .catch(() => { if (!cancelled) setTradesError(true); });
            }, 5_000);
          }
        }
      })
      .catch(() => { if (!cancelled) setError("network"); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      if (autoRetryTimer !== null) clearTimeout(autoRetryTimer);
    };
  }, [tokenAddress, chainId, retryCount]);

  // Trades-only retry effect — fires when the user clicks Retry inside the
  // trades error panel. Does not touch market state or the page-level loading
  // skeleton, so the rest of the page stays visible during the re-fetch.
  useEffect(() => {
    if (tradesRetryCount === 0 || !tokenAddress) return;
    let cancelled = false;
    setTradesLoading(true);
    setTradesError(false);
    const base = `/api/fair-launch`;
    fetch(`${base}/markets/${tokenAddress}/trades?chainId=${chainId}&limit=50`)
      .then(async (tRes) => {
        if (cancelled) return;
        if (tRes.ok) {
          const td = await tRes.json() as { trades: Trade[] };
          if (!cancelled) {
            setTrades(td.trades);
            setTradesFailedRetries(0);
          }
        } else {
          if (!cancelled) {
            setTradesError(true);
            setTradesFailedRetries((n) => n + 1);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTradesError(true);
          setTradesFailedRetries((n) => n + 1);
        }
      })
      .finally(() => { if (!cancelled) setTradesLoading(false); });
    return () => { cancelled = true; };
  }, [tradesRetryCount, tokenAddress, chainId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="h-8 w-48 rounded bg-panel/60 animate-pulse" />
        <div className="h-40 rounded-xl border border-line/40 bg-panel/30 animate-pulse" />
      </div>
    );
  }

  // Network error checked first: when fetch throws, market is still null,
  // so the not_found check would fire incorrectly without this ordering.
  if (error === "network") {
    return (
      <div className="max-w-xl mx-auto text-center space-y-4 py-16">
        {/* Breadcrumb remains visible so the user can navigate back */}
        <div className="flex items-center justify-center gap-2 text-sm text-muted">
          <Link to="/fair-launch" className="hover:text-plasma transition-colors">Fair Launch</Link>
          <span>/</span>
          <span className="text-ink/50 font-mono">{tokenAddress}</span>
        </div>
        <p className="text-danger text-sm font-medium">Could not load market — the server returned an error.</p>
        <p className="text-muted text-xs">The API may be temporarily unavailable.</p>
        <div className="flex items-center justify-center gap-3 mt-2">
          <Button onClick={() => setRetryCount((c) => c + 1)}>
            Try again
          </Button>
          <Link to="/fair-launch">
            <Button variant="ghost">← Back to Fair Launch</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (error === "not_found" || !market) {
    return (
      <div className="max-w-xl mx-auto text-center space-y-4 py-16">
        <Pill tone="warn">Market not indexed</Pill>
        <h1 className="font-display text-2xl font-bold">Market not found</h1>
        <p className="text-muted text-sm">
          This token address is not registered in the SYNAPZ fair-launch index.
          Either the contracts are not deployed yet, or the token was not created
          through the FairLaunchFactory.
        </p>
        <p className="font-mono text-xs text-muted/60 break-all">{tokenAddress}</p>
        <Link to="/fair-launch">
          <Button variant="ghost">← Back to Fair Launch</Button>
        </Link>
      </div>
    );
  }

  const realEth    = BigInt(market.realEthReserve || "0");
  const tokenRes   = BigInt(market.tokenReserve   || "0");
  const virtualEth = market.virtualEthReserve
    ? BigInt(market.virtualEthReserve)
    : FALLBACK_VIRTUAL_ETH;
  const gradThreshBigInt = market.graduationThreshold
    ? BigInt(market.graduationThreshold)
    : null;
  const spotPrice  = calcSpotPrice(realEth, tokenRes, virtualEth);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted">
        <Link to="/fair-launch" className="hover:text-plasma transition-colors">Fair Launch</Link>
        <span>/</span>
        <span className="text-ink font-mono">{market.symbol}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl font-bold">{market.name}</h1>
            <span className="font-mono text-lg text-muted">{market.symbol}</span>
            {market.graduated && <Pill tone="plasma">Graduated</Pill>}
            {market.paused && <Pill tone="warn">Paused</Pill>}
            {market.isSimulation && <Pill tone="muted">Simulation</Pill>}
            {isFallbackOracle(market) && <FallbackOracleBadge inline />}
          </div>
          <p className="font-mono text-xs text-muted/60 break-all">{market.tokenAddress}</p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <ShareButton
            tokenName={market.name}
            symbol={market.symbol}
            marketUrl={`${window.location.origin}/fair-launch/market/${chainId}/${market.tokenAddress}`}
            referrerAddress={connectedWallet ?? undefined}
          />
          <a href={explorerAddr(market.tokenAddress)} target="_blank" rel="noreferrer">
            <Button variant="ghost" className="text-xs">Token ↗</Button>
          </a>
          <a href={explorerAddr(market.marketAddress)} target="_blank" rel="noreferrer">
            <Button variant="ghost" className="text-xs">Market ↗</Button>
          </a>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        {/* Left column — stats + trades */}
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Spot price", value: spotPrice },
              { label: "ETH raised", value: formatEth(realEth) },
              { label: "Trades", value: trades.length.toString() },
              { label: "Creator", value: shortAddr(market.creatorAddress) },
            ].map(({ label, value }) => (
              <Card key={label} className="text-center space-y-1 py-3">
                <p className="text-xs text-muted">{label}</p>
                <p className="font-mono text-sm text-ink break-all">{value}</p>
              </Card>
            ))}
          </div>

          {/* Fallback oracle warning — shown when oracleRoundId == 0 */}
          {isFallbackOracle(market) && <FallbackOracleBadge />}

          {/* Graduation progress */}
          {!market.graduated && (
            <Card>
              <GradProgress
                realEth={realEth}
                graduationThreshold={gradThreshBigInt}
                graduationValuationUsd={market.graduationValuationUsd}
              />
            </Card>
          )}

          {/* Price history chart */}
          <Card>
            <PriceChart
              tokenAddress={market.tokenAddress}
              chainId={chainId}
              symbol={market.symbol}
            />
          </Card>

          {/* Community comments (Gate 8) */}
          <MarketComments chainId={chainId} tokenAddress={market.tokenAddress} />

          {/* Trade history */}
          <Card className="overflow-hidden p-0">
            <div className="px-5 py-3 border-b border-line/30 flex items-center justify-between">
              <h3 className="font-display font-semibold text-sm">Recent trades</h3>
              {!tradesError && trades.length === 0 && (
                <span className="text-xs text-muted">No trades yet</span>
              )}
            </div>
            {tradesError ? (
              <div
                className="px-5 py-4 text-center text-sm text-amber-400/80 space-y-2"
                data-testid="trades-error-notice"
              >
                {tradesFailedRetries >= 2 ? (
                  <p data-testid="trades-persistent-error">Still unavailable — try refreshing the page.</p>
                ) : (
                  <p>Trade history could not be loaded. Market data and trading are unaffected.</p>
                )}
                <button
                  className="text-xs underline hover:text-amber-300 transition-colors disabled:opacity-50"
                  data-testid="trades-retry-button"
                  disabled={tradesLoading}
                  onClick={() => setTradesRetryCount((c) => c + 1)}
                >
                  {tradesLoading ? "Retrying…" : "Retry"}
                </button>
              </div>
            ) : trades.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[400px]">
                  <thead>
                    <tr className="text-[10px] text-muted/60 uppercase border-b border-line/20">
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">ETH</th>
                      <th className="px-3 py-2 text-right">Tokens</th>
                      <th className="px-3 py-2 text-left">Tx</th>
                      <th className="px-3 py-2 text-left">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map(t => <TradeRow key={t.id} t={t} />)}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-5 py-8 text-center text-sm text-muted/60">
                No trades indexed yet
              </div>
            )}
          </Card>
        </div>

        {/* Right column — trading panel */}
        <div className="space-y-4">
          <TradingPanel market={market} />

          {/* Market info */}
          <Card className="space-y-2">
            <h3 className="text-xs font-semibold text-muted uppercase tracking-wide">Market info</h3>
            {([
              ["Market contract", shortAddr(market.marketAddress)],
              ["Token contract", shortAddr(market.tokenAddress)],
              ["Creator", shortAddr(market.creatorAddress)],
              ["Created at block", market.blockNumber.toString()],
              ["Launch tx", shortAddr(market.txHash)],
              ...(market.oraclePrice != null
                ? [["Oracle at launch", `${formatOraclePrice(market.oraclePrice, market.oracleDecimals)} / ETH`]]
                : []),
              ...(market.oracleRoundId != null
                ? [["Oracle round ID", market.oracleRoundId === "0" ? "0 (fallback)" : market.oracleRoundId]]
                : []),
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs gap-2">
                <span className="text-muted">{k}</span>
                <span className="font-mono text-ink">{v}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
